import { useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Monday-first, same order as backend weekly_hours
export const DAY_NAMES = {
  lv: ["Pirmdiena", "Otrdiena", "Trešdiena", "Ceturtdiena", "Piektdiena", "Sestdiena", "Svētdiena"],
  ru: ["Понедельник", "Вторник", "Среда", "Четверг", "Пятница", "Суббота", "Воскресенье"],
  en: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
};

const CLOSED = { lv: "Slēgts", ru: "Закрыто", en: "Closed" };
const TODAY = { lv: "Šodien", ru: "Сегодня", en: "Today" };
const TOMORROW = { lv: "Rīt", ru: "Завтра", en: "Tomorrow" };

export const EMPTY_SCHEDULE = { holidays: [], special_hours: {}, weekly_hours: [] };

const pad = (n) => String(n).padStart(2, "0");
export const fmtHour = (h) => (Number(h) === 24 ? "00:00" : `${pad(h)}:00`);
const dateKey = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

export const useSchedule = (enabled = true) => {
  const [schedule, setSchedule] = useState(EMPTY_SCHEDULE);
  useEffect(() => {
    if (!enabled) return;
    axios.get(`${API}/schedule`).then((res) => setSchedule({ ...EMPTY_SCHEDULE, ...res.data })).catch(() => {});
  }, [enabled]);
  return schedule;
};

export const hoursForDate = (schedule, d) => {
  const key = dateKey(d);
  if (schedule.holidays.includes(key)) return null;
  if (schedule.special_hours[key]) return schedule.special_hours[key];
  const w = schedule.weekly_hours[(d.getDay() + 6) % 7];
  return !w || w.closed ? null : { open: w.open, close: w.close };
};

// Groups consecutive days with identical hours: [{ label, value }]
export const groupWeeklyHours = (weekly, language) => {
  const names = DAY_NAMES[language] || DAY_NAMES.lv;
  const value = (w) => (w.closed ? CLOSED[language] || CLOSED.lv : `${fmtHour(w.open)} – ${fmtHour(w.close)}`);
  const groups = [];
  weekly.forEach((w, i) => {
    const last = groups[groups.length - 1];
    if (last && last.value === value(w)) last.end = i;
    else groups.push({ start: i, end: i, value: value(w), closed: w.closed });
  });
  return groups.map((g) => ({
    label: g.start === g.end ? names[g.start] : `${names[g.start]} – ${names[g.end]}`,
    value: g.value,
    closed: g.closed,
  }));
};

// 30-min slots for the next two open days (skips closed days / holidays), Riga time
export const generateTimeSlots = (schedule, language, asapLabel) => {
  const slots = asapLabel ? [{ value: "", label: asapLabel }] : [];
  if (!schedule.weekly_hours.length) return slots;
  const realNow = new Date();
  const nowRiga = new Date(realNow.toLocaleString("en-US", { timeZone: "Europe/Riga" }));
  const rigaOffsetMs = Math.round((realNow.getTime() - nowRiga.getTime()) / 60000) * 60000;
  const minDelivery = new Date(nowRiga.getTime() + 60 * 60 * 1000);
  let openDays = 0;
  for (let offset = 0; offset < 15 && openDays < 2; offset++) {
    const day = new Date(nowRiga);
    day.setDate(day.getDate() + offset);
    const hours = hoursForDate(schedule, day);
    if (!hours) continue;
    const dayLabel = offset === 0 ? TODAY[language] || TODAY.lv
      : offset === 1 ? TOMORROW[language] || TOMORROW.lv
      : `${(DAY_NAMES[language] || DAY_NAMES.lv)[(day.getDay() + 6) % 7]} ${pad(day.getDate())}.${pad(day.getMonth() + 1)}`;
    let added = 0;
    for (let hour = hours.open; hour < hours.close; hour++) {
      for (const minute of [0, 30]) {
        const slot = new Date(day);
        slot.setHours(hour, minute, 0, 0);
        if (slot <= nowRiga || (offset === 0 && slot < minDelivery)) continue;
        slots.push({ value: new Date(slot.getTime() + rigaOffsetMs).toISOString(), label: `${dayLabel} ${pad(hour)}:${pad(minute)}` });
        added++;
      }
    }
    if (added) openDays++;
  }
  return slots;
};
